import { useEffect, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { Dialog } from '@base-ui/react/dialog'
import {
  operationalExpenseSchema,
  type OperationalExpenseFormData,
} from '@/validators/operational-expense'
import { useCreateOperationalExpense } from '@/hooks/useOperationalExpenses'
import { useCashAccounts } from '@/hooks/useCashAccounts'
import { useCashCategories } from '@/hooks/useCashCategories'
import { useVehicles } from '@/hooks/useVehicles'
import { useFreights } from '@/hooks/useFreights'
import { MoneyInput } from '@/components/form/money-input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  'disabled:cursor-not-allowed disabled:opacity-50',
)

function apiError(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? fallback
  )
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

interface Props {
  open: boolean
  onClose: () => void
  /** Pré-seleciona categoria de sistema (FUEL, MAINTENANCE, TOLL) via nome */
  presetCategoryName?: 'Combustível' | 'Manutenção' | 'Pedágio' | null
  presetVehicleId?: string | null
  presetFreightId?: string | null
}

export function OperationalExpenseModal({
  open,
  onClose,
  presetCategoryName = null,
  presetVehicleId = null,
  presetFreightId = null,
}: Props) {
  const createMutation = useCreateOperationalExpense()
  const { data: accountsData } = useCashAccounts({ active: true, perPage: 100 })
  const { data: categoriesData } = useCashCategories({ kind: 'OUTFLOW', active: true, perPage: 100 })
  const { data: vehiclesData } = useVehicles({ active: true, perPage: 100 })
  const { data: freightsData } = useFreights({ perPage: 50 })

  const accounts = accountsData?.data ?? []
  const categories = categoriesData?.data ?? []
  const vehicles = vehiclesData?.data ?? []
  const freights = freightsData?.data ?? []

  const defaultCategoryId = useMemo(() => {
    if (!presetCategoryName) return ''
    return categories.find((c) => c.name === presetCategoryName)?.id ?? ''
  }, [presetCategoryName, categories])

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<OperationalExpenseFormData>({
    resolver: zodResolver(operationalExpenseSchema),
    defaultValues: {
      categoryId: '',
      accountId: '',
      vehicleId: null,
      freightId: null,
      amount: '' as unknown as number,
      date: todayISO(),
      description: '',
      supplier: '',
      documentNumber: '',
      method: 'PIX',
      notes: '',
    },
  })

  useEffect(() => {
    if (open) {
      const fallbackAccount = accounts.find((a) => a.isDefault)?.id ?? accounts[0]?.id ?? ''
      reset({
        categoryId: defaultCategoryId,
        accountId: fallbackAccount,
        vehicleId: presetVehicleId ?? null,
        freightId: presetFreightId ?? null,
        amount: '' as unknown as number,
        date: todayISO(),
        description: '',
        supplier: '',
        documentNumber: '',
        method: 'PIX',
        notes: '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultCategoryId])

  const selectedFreightId = watch('freightId')
  useEffect(() => {
    if (!selectedFreightId) return
    const freight = freights.find((f) => f.id === selectedFreightId)
    if (freight?.vehicleId) {
      setValue('vehicleId', freight.vehicleId, { shouldDirty: true })
    }
  }, [selectedFreightId, freights, setValue])

  async function onSubmit(data: OperationalExpenseFormData) {
    try {
      await createMutation.mutateAsync({
        ...data,
        vehicleId: data.vehicleId || null,
        freightId: data.freightId || null,
        supplier: data.supplier || null,
        documentNumber: data.documentNumber || null,
        notes: data.notes || null,
        date: new Date(data.date).toISOString(),
      })
      toast.success('Despesa registrada!')
      onClose()
    } catch (err) {
      toast.error(apiError(err, 'Erro ao registrar despesa'))
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            Nova despesa operacional
          </Dialog.Title>
          <Dialog.Description className="text-sm text-muted-foreground">
            Lançada como saída no fluxo de caixa.
          </Dialog.Description>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="oe-category">Categoria *</Label>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }) => (
                    <select id="oe-category" value={field.value} onChange={field.onChange} className={selectClass}>
                      <option value="" disabled>Selecione…</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  )}
                />
                {errors.categoryId && (
                  <p className="text-xs text-destructive">{errors.categoryId.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="oe-account">Pago pela conta *</Label>
                <Controller
                  name="accountId"
                  control={control}
                  render={({ field }) => (
                    <select id="oe-account" value={field.value} onChange={field.onChange} className={selectClass}>
                      <option value="" disabled>Selecione…</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  )}
                />
                {errors.accountId && (
                  <p className="text-xs text-destructive">{errors.accountId.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="oe-amount">Valor *</Label>
                <MoneyInput id="oe-amount" error={!!errors.amount} {...register('amount')} />
                {errors.amount && <p className="text-xs text-destructive">{errors.amount.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="oe-date">Data *</Label>
                <Input id="oe-date" type="date" aria-invalid={!!errors.date} {...register('date')} />
                {errors.date && <p className="text-xs text-destructive">{errors.date.message}</p>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="oe-description">Descrição *</Label>
              <Input
                id="oe-description"
                placeholder="Ex.: Abastecimento de diesel na BR-101"
                aria-invalid={!!errors.description}
                {...register('description')}
              />
              {errors.description && (
                <p className="text-xs text-destructive">{errors.description.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="oe-freight">Frete (opcional)</Label>
                <Controller
                  name="freightId"
                  control={control}
                  render={({ field }) => (
                    <select
                      id="oe-freight"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      className={selectClass}
                    >
                      <option value="">—</option>
                      {freights.map((f) => (
                        <option key={f.id} value={f.id}>
                          F-{f.code} · {f.company.tradeName}
                        </option>
                      ))}
                    </select>
                  )}
                />
                <p className="text-xs text-muted-foreground">
                  Selecione o frete primeiro — o veículo é preenchido automaticamente.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="oe-vehicle">Veículo (opcional)</Label>
                <Controller
                  name="vehicleId"
                  control={control}
                  render={({ field }) => (
                    <select
                      id="oe-vehicle"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      className={selectClass}
                    >
                      <option value="">—</option>
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.id}>{v.plate}</option>
                      ))}
                    </select>
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="oe-supplier">Fornecedor</Label>
                <Input id="oe-supplier" placeholder="Posto, oficina…" {...register('supplier')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="oe-doc">Nº do documento</Label>
                <Input id="oe-doc" placeholder="NF, recibo, cupom" {...register('documentNumber')} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="oe-method">Forma de pagamento *</Label>
              <Controller
                name="method"
                control={control}
                render={({ field }) => (
                  <select id="oe-method" value={field.value} onChange={field.onChange} className={selectClass}>
                    <option value="PIX">PIX</option>
                    <option value="BANK_TRANSFER">Transferência</option>
                    <option value="BANK_SLIP">Boleto</option>
                    <option value="CASH">Dinheiro</option>
                    <option value="OTHER">Outro</option>
                  </select>
                )}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="oe-notes">Observações</Label>
              <Input id="oe-notes" placeholder="Opcional" {...register('notes')} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={createMutation.isPending}>
                Cancelar
              </Button>
              <LoadingButton type="submit" loading={createMutation.isPending} loadingText="Registrando…">
                Registrar despesa
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
